import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Alert,
    Snackbar,
    Box,
    Paper,
    Stack,
    CircularProgress
} from '@mui/material'
import dayjs from 'dayjs'
import { toAdapterLocale } from '@/utils/locale'
import { AvailableShift, GetAvailableShiftsParams } from '@/core/models/planning.model'
import AvailableShiftsList from '../components/AvailableShiftsList'
import PlanningFilterContainer from './PlanningFilterContainer'
import ViewToggle, { ViewMode } from '../components/ViewToggle'
import ShiftCalendarView from '../components/ShiftCalendarView'
import { useCheckShiftConflictsQuery, useClaimShiftMutation, useGetAvailableShiftsQuery } from '@/core/services/shift-marketplace'
import { getApiErrorMessage } from '@/utils/errors'

export function AvailableShiftsContainer() {
    const { t, i18n } = useTranslation()
    const locale = toAdapterLocale(i18n.language)
    const [page, setPage] = useState(1)
    const [limit] = useState(9)
    const [viewMode, setViewMode] = useState<ViewMode>('list')
    const initializedRef = useRef(false)

    // Initialize filters with the current week's date range
    const today = dayjs()
    const startOfWeek = today.startOf('week').toDate()
    const endOfWeek = today.endOf('week').toDate()

    const [filters, setFilters] = useState<Partial<GetAvailableShiftsParams>>({
        sortBy: 'startTime',
        sortDirection: 'asc',
        startDate: startOfWeek.toISOString(),
        endDate: endOfWeek.toISOString()
    })

    const [selectedShift, setSelectedShift] = useState<AvailableShift | null>(null)
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false)
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const { data, isLoading, error, refetch } = useGetAvailableShiftsQuery({
        ...filters,
        page: Number(page),
        limit: Number(limit)
    }, {
        // This ensures the component always shows fresh data
        refetchOnMountOrArgChange: true
    })

    const [claimShift, { isLoading: isClaimingShift }] = useClaimShiftMutation()

    // Skip conflict check if no shift is selected
    const { data: conflictData, isLoading: isCheckingConflicts } = useCheckShiftConflictsQuery(
        selectedShift ? {
            startTime: selectedShift.startTime.toString(),
            endTime: selectedShift.endTime.toString()
        } : { startTime: '', endTime: '' },
        { skip: !selectedShift || !confirmDialogOpen }
    )

    const handlePageChange = (newPage: number) => {
        setPage(newPage)
    }

    const handleFilterChange = (newFilters: Partial<GetAvailableShiftsParams>) => {
        setFilters(newFilters)
        setPage(1)
    }

    const handlePlanningChange = (planningId: string | null) => {
        if (planningId) {
            handleFilterChange({ ...filters, planningId })
        } else {
            const { planningId, ...restFilters } = filters
            handleFilterChange(restFilters)
        }
    }

    const handleViewModeChange = (mode: ViewMode) => {
        setViewMode(mode)
    }

    const handleWeekChange = (startDate: Date, endDate: Date) => {
        // Only update filters if the dates have actually changed
        // This prevents unnecessary re-renders
        if (
            filters.startDate !== startDate.toISOString() ||
            filters.endDate !== endDate.toISOString()
        ) {
            // Update filters with new date range when week changes in calendar view
            handleFilterChange({
                ...filters,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString()
            })
        }
    }

    const handleClaimClick = (shift: AvailableShift) => {
        setSelectedShift(shift)
        setConfirmDialogOpen(true)
    }

    const handleConfirmClaim = async () => {
        if (selectedShift) {
            try {
                await claimShift({
                    planningId: selectedShift.planningId,
                    shiftTemplateId: selectedShift.shiftTemplateId,
                    startTime: selectedShift.startTime,
                    endTime: selectedShift.endTime
                }).unwrap()

                // Close the dialog and show success message
                setConfirmDialogOpen(false)
                setSuccessSnackbarOpen(true)
                setSelectedShift(null)
            } catch (err: any) {
                // The backend returns a typed `code` on claim rejections (error messages themselves
                // are sanitized to generic text since MED-1705), so map it to a localized message.
                const code = (err as { data?: { code?: string } })?.data?.code
                const codeMessages: Record<string, string> = {
                    'shiftMarketplace.alreadyFull': t('page.shiftMarketplace.claimError.alreadyFull', 'This shift was just taken and is now full.'),
                    'shiftMarketplace.alreadyAssigned': t('page.shiftMarketplace.claimError.alreadyAssigned', 'You are already assigned to this shift.'),
                    'shiftMarketplace.scheduleConflict': t('page.shiftMarketplace.claimError.scheduleConflict', 'You have a scheduling conflict during this time.'),
                    'shiftMarketplace.pastShift': t('page.shiftMarketplace.claimError.pastShift', 'This shift is already in the past.'),
                    'shiftMarketplace.planningEnded': t('page.shiftMarketplace.claimError.planningEnded', 'This planning has already ended.'),
                    'shiftMarketplace.outsideRange': t('page.shiftMarketplace.claimError.outsideRange', 'This shift is outside the planning period.'),
                }
                setErrorMessage((code && codeMessages[code]) || getApiErrorMessage(err, t('page.shiftMarketplace.errorOccurred', 'An error occurred')))
                setErrorSnackbarOpen(true)
                setConfirmDialogOpen(false)
                // The claim failed because the displayed list was out of date (e.g. someone else
                // took the shift) — refresh it so the stale slot disappears. RTK Query only
                // invalidates tags on successful mutations, so refetch explicitly.
                refetch()
            }
        }
    }

    const handleCancelClaim = () => {
        setConfirmDialogOpen(false)
        setSelectedShift(null)
    }

    const handleCloseSuccessSnackbar = () => {
        setSuccessSnackbarOpen(false)
    }

    const handleCloseErrorSnackbar = () => {
        setErrorSnackbarOpen(false)
    }

    // Determine if there are conflicts
    const hasConflicts = conflictData?.hasConflict || false

    return (
        <>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} data-testid="available-shifts-container">
                <Box sx={{ px: 1, py: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} data-testid="available-shifts-toolbar">
                        <Box sx={{ width: { xs: '100%', sm: '250px' } }} data-testid="available-shifts-planning-filter">
                            <PlanningFilterContainer
                                selectedPlanningId={filters.planningId || null}
                                onPlanningChange={handlePlanningChange}
                            />
                        </Box>
                        <Box data-testid="available-shifts-view-toggle">
                            <ViewToggle
                                viewMode={viewMode}
                                onViewModeChange={handleViewModeChange}
                            />
                        </Box>
                    </Stack>
                </Box>

                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    {viewMode === 'list' ? (
                        <AvailableShiftsList
                            shifts={data?.items || []}
                            isLoading={isLoading || isClaimingShift}
                            error={error}
                            totalItems={data?.total || 0}
                            page={page}
                            limit={limit}
                            onPageChange={handlePageChange}
                            onFilterChange={handleFilterChange}
                            onClaimShift={handleClaimClick}
                            filters={filters}
                        />
                    ) : (
                        <ShiftCalendarView
                            shifts={data?.items || []}
                            isLoading={isLoading || isClaimingShift}
                            error={error}
                            onClaimShift={handleClaimClick}
                            onWeekChange={handleWeekChange}
                        />
                    )}
                </Box>
            </Box>

            <Dialog
                open={confirmDialogOpen}
                onClose={handleCancelClaim}
                aria-labelledby="claim-shift-dialog-title"
                aria-describedby="claim-shift-dialog-description"
            >
                <DialogTitle id="claim-shift-dialog-title">
                    {t('page.shiftMarketplace.confirmClaim', 'Confirm Claim')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="claim-shift-dialog-description">
                        {selectedShift && (
                            <>
                                {t('page.shiftMarketplace.confirmClaimMessage', 'Are you sure you want to claim this shift?')}
                                <br /><br />
                                <strong>{dayjs(selectedShift.startTime).locale(locale).format('dddd D MMMM YYYY')}</strong>
                                <br />
                                {dayjs(selectedShift.startTime).locale(locale).format('HH:mm')} - {dayjs(selectedShift.endTime).locale(locale).format('HH:mm')}
                                <br />
                                {selectedShift.location}
                                <br />
                                {selectedShift.consultationReason}
                                {selectedShift.planningName && (
                                    <>
                                        <br />
                                        <strong>{t('page.shiftMarketplace.planning', 'Planning')}:</strong> {selectedShift.planningName}
                                    </>
                                )}
                            </>
                        )}
                    </DialogContentText>

                    {isCheckingConflicts && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {hasConflicts && !isCheckingConflicts && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {t('page.shiftMarketplace.conflictWarning', 'Warning: This shift conflicts with your existing schedule.')}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelClaim} disabled={isClaimingShift}>
                        {t('common:button.cancel.label')}
                    </Button>
                    <Button
                        onClick={handleConfirmClaim}
                        color={hasConflicts ? "warning" : "primary"}
                        disabled={isClaimingShift || isCheckingConflicts}
                        autoFocus
                    >
                        {isClaimingShift ? t('page.shiftMarketplace.processing', 'Processing...') : t('page.shiftMarketplace.claim', 'Claim')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={successSnackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSuccessSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSuccessSnackbar} severity="success" sx={{ width: '100%' }}>
                    {t('page.shiftMarketplace.shiftClaimedSuccess', 'Shift claimed successfully!')}
                </Alert>
            </Snackbar>

            <Snackbar
                open={errorSnackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseErrorSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseErrorSnackbar} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    )
}

export default AvailableShiftsContainer
